import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, UserPlus, Calendar, Package, LayoutDashboard, Users, Car, CircleDollarSign, Settings, FileText } from 'lucide-react';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/Command';
import { Dialog, DialogContent, DialogTitle } from '../ui/Dialog';
import { mockServiceOrders, mockClients, mockVehicles } from '../../data/mockData';
import { formatPlate } from '../../lib/utils';
interface CommandPaletteProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export function CommandPalette({
  open,
  setOpen
}: CommandPaletteProps) {
  const navigate = useNavigate();
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, setOpen]);
  const runCommand = (command: () => void) => {
    setOpen(false);
    command();
  };
  return <Dialog open={open} onOpenChange={setOpen} data-id="element-120">
      <DialogContent className="p-0 overflow-hidden max-w-2xl" showCloseButton={false} data-id="element-121">
        <DialogTitle className="sr-only" data-id="element-122">Busca global</DialogTitle>
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground" data-id="element-123">
          <CommandInput placeholder="Buscar OS, cliente, veículo ou navegar..." data-id="element-124" />
          <CommandList data-id="element-125">
            <CommandGroup heading="Ações Rápidas" data-id="element-126">
              <CommandItem onSelect={() => runCommand(() => navigate('/app/ordens'))} data-id="element-127">
                <Wrench className="mr-2 h-4 w-4 text-primary" data-id="element-128" />
                <span data-id="element-129">Nova OS</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/app/clientes'))} data-id="element-130">
                <UserPlus className="mr-2 h-4 w-4 text-primary" data-id="element-131" />
                <span data-id="element-132">Novo Cliente</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/app/agenda'))} data-id="element-133">
                <Calendar className="mr-2 h-4 w-4 text-primary" data-id="element-134" />
                <span data-id="element-135">Novo Agendamento</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/app/estoque'))} data-id="element-136">
                <Package className="mr-2 h-4 w-4 text-primary" data-id="element-137" />
                <span data-id="element-138">Adicionar item ao estoque</span>
              </CommandItem>
            </CommandGroup>

            <CommandGroup heading="Páginas" data-id="element-139">
              <CommandItem onSelect={() => runCommand(() => navigate('/app'))} data-id="element-140">
                <LayoutDashboard className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-141" />
                <span data-id="element-142">Dashboard</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/app/ordens'))} data-id="element-143">
                <FileText className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-144" />
                <span data-id="element-145">Ordens de Serviço</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/app/clientes'))} data-id="element-146">
                <Users className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-147" />
                <span data-id="element-148">Clientes</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/app/veiculos'))} data-id="element-149">
                <Car className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-150" />
                <span data-id="element-151">Veículos</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/app/estoque'))} data-id="element-152">
                <Package className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-153" />
                <span data-id="element-154">Estoque</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/app/financeiro'))} data-id="element-155">
                <CircleDollarSign className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-156" />
                <span data-id="element-157">Financeiro</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/app/configuracoes'))} data-id="element-158">
                <Settings className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-159" />
                <span data-id="element-160">Configurações</span>
              </CommandItem>
            </CommandGroup>

            <CommandGroup heading="Ordens de Serviço" data-id="element-161">
              {mockServiceOrders.slice(0, 5).map(os => {
              const client = mockClients.find(c => c.id === os.clientId);
              const vehicle = mockVehicles.find(v => v.id === os.vehicleId);
              return <CommandItem key={os.id} onSelect={() => runCommand(() => navigate(`/app/ordens/${os.id}`))} data-id="element-162">
                    <FileText className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-163" />
                    <span className="font-mono" data-id="element-164">{os.number}</span>
                    <span className="ml-auto text-xs text-muted-foreground" data-id="element-165">
                      {vehicle?.plate} • {client?.name}
                    </span>
                  </CommandItem>;
            })}
            </CommandGroup>

            <CommandGroup heading="Clientes" data-id="element-166">
              {mockClients.slice(0, 5).map(client => <CommandItem key={client.id} onSelect={() => runCommand(() => navigate(`/app/clientes/${client.id}`))} data-id="element-167">
                  <Users className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-168" />
                  <span data-id="element-169">{client.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground" data-id="element-170">
                    {client.phone}
                  </span>
                </CommandItem>)}
            </CommandGroup>

            <CommandGroup heading="Veículos" data-id="element-171">
              {mockVehicles.slice(0, 5).map(vehicle => <CommandItem key={vehicle.id} onSelect={() => runCommand(() => navigate(`/app/veiculos/${vehicle.id}`))} data-id="element-172">
                  <Car className="mr-2 h-4 w-4 text-muted-foreground" data-id="element-173" />
                  <span data-id="element-174">
                    {vehicle.brand} {vehicle.model}
                  </span>
                  <span className="ml-auto text-xs font-mono text-muted-foreground" data-id="element-175">
                    {formatPlate(vehicle.plate)}
                  </span>
                </CommandItem>)}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>;
}